import React, { useState, useEffect } from 'react';
import {
    Modal,
    Form,
    Input,
    Select,
    Button,
    Space,
    Typography,
    Alert,
    Divider,
    Card,
    message,
    Spin,
} from 'antd';
import { DatabaseOutlined, CodeOutlined } from '@ant-design/icons';
import { SqliteFieldConfig } from '../../../common/typescript/models/CollectFormV2';
import initSqlJs from 'sql.js';

const { Text, Title } = Typography;
const { TextArea } = Input;

type SqliteQueryConfigModalProps = {
    visible: boolean;
    uploadedDatabases: string[]; // File names
    currentConfig?: SqliteFieldConfig;
    onClose: () => void;
    onSave: (config: SqliteFieldConfig) => void;
    sqliteFiles: File[]; // Actual File objects
};

export const SqliteQueryConfigModal: React.FC<SqliteQueryConfigModalProps> = ({
    visible,
    uploadedDatabases,
    currentConfig,
    onClose,
    onSave,
    sqliteFiles,
}) => {
    const [form] = Form.useForm();
    const [selectedDatabase, setSelectedDatabase] = useState<string>('');
    const [tables, setTables] = useState<string[]>([]);
    const [columns, setColumns] = useState<string[]>([]);
    const [loadingSchema, setLoadingSchema] = useState(false);
    const [selectedTable, setSelectedTable] = useState<string>('');

    useEffect(() => {
        if (currentConfig) {
            form.setFieldsValue({
                database: currentConfig.database,
                table: currentConfig.table,
                valueColumn: currentConfig.valueColumn,
                labelColumn: currentConfig.labelColumn,
                where: currentConfig.where || '',
                orderBy: currentConfig.orderBy || '',
            });
            setSelectedDatabase(currentConfig.database);
            setSelectedTable(currentConfig.table);
        } else {
            form.resetFields();
            setSelectedDatabase('');
            setSelectedTable('');
        }
    }, [currentConfig, form, visible]);

    // Load tables and columns from SQLite file
    const loadSqliteSchema = async (fileName: string) => {
        try {
            setLoadingSchema(true);
            
            // Find the actual File object
            const file = sqliteFiles.find(f => f.name === fileName);
            if (!file) {
                message.error('Không tìm thấy file SQLite');
                return;
            }

            // Read file as ArrayBuffer
            const arrayBuffer = await file.arrayBuffer();
            const uint8Array = new Uint8Array(arrayBuffer);

            // Initialize sql.js
            const SQL = await initSqlJs({
                locateFile: (file) => `https://sql.js.org/dist/${file}`
            });

            // Open database
            const db = new SQL.Database(uint8Array);

            // Get all tables
            const tablesResult = db.exec(
                "SELECT name FROM sqlite_master WHERE type='table' AND name NOT LIKE 'sqlite_%' ORDER BY name"
            );

            if (tablesResult.length > 0 && tablesResult[0].values.length > 0) {
                const tableNames = tablesResult[0].values.map(row => row[0] as string);
                setTables(tableNames);
            } else {
                setTables([]);
            }

            db.close();
        } catch (error) {
            console.error('Error loading SQLite schema:', error);
            message.error('Không thể đọc cấu trúc SQLite');
            setTables([]);
        } finally {
            setLoadingSchema(false);
        }
    };

    // Load columns when table is selected
    const loadTableColumns = async (tableName: string) => {
        try {
            const fileName = selectedDatabase;
            const file = sqliteFiles.find(f => f.name === fileName);
            if (!file) return;

            const arrayBuffer = await file.arrayBuffer();
            const uint8Array = new Uint8Array(arrayBuffer);

            const SQL = await initSqlJs({
                locateFile: (file) => `https://sql.js.org/dist/${file}`
            });

            const db = new SQL.Database(uint8Array);

            // Get table info
            const columnsResult = db.exec(`PRAGMA table_info(${tableName})`);

            if (columnsResult.length > 0 && columnsResult[0].values.length > 0) {
                // Column name is at index 1 in PRAGMA table_info result
                const columnNames = columnsResult[0].values.map(row => row[1] as string);
                setColumns(columnNames);
            } else {
                setColumns([]);
            }

            db.close();
        } catch (error) {
            console.error('Error loading table columns:', error);
            message.error('Không thể đọc cột của bảng');
            setColumns([]);
        }
    };

    // Handle database selection
    const handleDatabaseChange = (value: string) => {
        setSelectedDatabase(value);
        setSelectedTable('');
        setTables([]);
        setColumns([]);
        form.setFieldsValue({ table: undefined, valueColumn: undefined, labelColumn: undefined });
        loadSqliteSchema(value);
    };

    // Handle table selection
    const handleTableChange = (value: string) => {
        setSelectedTable(value);
        setColumns([]);
        form.setFieldsValue({ valueColumn: undefined, labelColumn: undefined });
        loadTableColumns(value);
    };

    const handleSave = () => {
        form
            .validateFields()
            .then((values) => {
                const config: SqliteFieldConfig = {
                    database: values.database,
                    table: values.table,
                    valueColumn: values.valueColumn,
                    labelColumn: values.labelColumn,
                    where: values.where?.trim() || undefined,
                    orderBy: values.orderBy?.trim() || undefined,
                };
                onSave(config);
                form.resetFields();
            })
            .catch((err) => {
                console.error('Validation failed:', err);
            });
    };

    return (
        <Modal
            title={
                <Space>
                    <DatabaseOutlined />
                    <span>Cấu hình truy vấn SQLite</span>
                </Space>
            }
            open={visible}
            onCancel={onClose}
            width={700}
            footer={
                <Space>
                    <Button onClick={onClose}>Hủy</Button>
                    <Button type="primary" onClick={handleSave}>
                        Lưu cấu hình
                    </Button>
                </Space>
            }
        >
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                {uploadedDatabases.length === 0 && (
                    <Alert
                        message="Chưa có cơ sở dữ liệu SQLite"
                        description="Vui lòng tải lên file SQLite trước bằng sidebar bên phải."
                        type="warning"
                        showIcon
                    />
                )}

                <Form form={form} layout="vertical">
                    {/* Database Selection */}
                    <Form.Item
                        name="database"
                        label="Cơ sở dữ liệu SQLite"
                        rules={[{ required: true, message: 'Vui lòng chọn cơ sở dữ liệu' }]}
                    >
                        <Select
                            placeholder="Chọn CSDL đã tải lên"
                            onChange={handleDatabaseChange}
                            disabled={uploadedDatabases.length === 0}
                        >
                            {uploadedDatabases.map((db) => (
                                <Select.Option key={db} value={db}>
                                    <Space>
                                        <DatabaseOutlined />
                                        <Text>{db}</Text>
                                    </Space>
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Divider>Cấu hình truy vấn</Divider>

                    {/* Table Name */}
                    <Form.Item
                        name="table"
                        label="Tên bảng"
                        rules={[{ required: true, message: 'Vui lòng chọn bảng' }]}
                        extra={loadingSchema ? "Đang tải danh sách bảng..." : `Chọn bảng từ CSDL${tables.length > 0 ? ` (${tables.length} bảng)` : ''}`}
                    >
                        <Select 
                            placeholder="Chọn bảng" 
                            disabled={!selectedDatabase || loadingSchema || tables.length === 0}
                            onChange={handleTableChange}
                            loading={loadingSchema}
                            showSearch
                        >
                            {tables.map((table) => (
                                <Select.Option key={table} value={table}>
                                    {table}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    {/* Value Column */}
                    <Form.Item
                        name="valueColumn"
                        label="Cột giá trị"
                        rules={[{ required: true, message: 'Vui lòng chọn cột giá trị' }]}
                        extra={`Cột dùng làm giá trị tùy chọn${columns.length > 0 ? ` (${columns.length} cột)` : ''}`}
                    >
                        <Select 
                            placeholder="Chọn cột giá trị" 
                            disabled={!selectedTable || columns.length === 0}
                            showSearch
                        >
                            {columns.map((col) => (
                                <Select.Option key={col} value={col}>
                                    {col}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    {/* Label Column */}
                    <Form.Item
                        name="labelColumn"
                        label="Cột nhãn"
                        rules={[{ required: true, message: 'Vui lòng chọn cột nhãn' }]}
                        extra={`Cột hiển thị cho người dùng${columns.length > 0 ? ` (${columns.length} cột)` : ''}`}
                    >
                        <Select 
                            placeholder="Chọn cột nhãn" 
                            disabled={!selectedTable || columns.length === 0}
                            showSearch
                        >
                            {columns.map((col) => (
                                <Select.Option key={col} value={col}>
                                    {col}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Divider>Bộ lọc tùy chọn</Divider>

                    {/* WHERE Clause */}
                    <Form.Item
                        name="where"
                        label="Điều kiện WHERE (Tùy chọn)"
                        extra={
                            <Space direction="vertical" size={0}>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    Lọc kết quả bằng điều kiện WHERE. Dùng ${'{id_truong}'} cho giá trị động.
                                </Text>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    Ví dụ: ma_tinh = ${'{tinh}'}
                                </Text>
                            </Space>
                        }
                    >
                        <TextArea
                            rows={2}
                            placeholder="vd: ma_tinh = ${tinh}"
                        />
                    </Form.Item>

                    {/* ORDER BY Clause */}
                    <Form.Item
                        name="orderBy"
                        label="Sắp xếp ORDER BY (Tùy chọn)"
                        extra="Sắp xếp kết quả (vd: ten_tinh ASC, ma_huyen DESC)"
                    >
                        <Input placeholder="vd: ten_tinh ASC" />
                    </Form.Item>
                </Form>

                {/* Example Preview */}
                {selectedDatabase && (
                    <Card size="small" title={<Space><CodeOutlined /> Xem trước truy vấn</Space>}>
                        <Text code style={{ fontSize: '12px', whiteSpace: 'pre-wrap' }}>
                            {`SELECT ${form.getFieldValue('valueColumn') || 'cot_gia_tri'}, ${form.getFieldValue('labelColumn') || 'cot_nhan'}\\nFROM ${form.getFieldValue('table') || 'ten_bang'}${form.getFieldValue('where')
                                    ? `\\nWHERE ${form.getFieldValue('where')}`
                                    : ''
                                }${form.getFieldValue('orderBy')
                                    ? `\\nORDER BY ${form.getFieldValue('orderBy')}`
                                    : ''
                                }`}
                        </Text>
                    </Card>
                )}

                {/* Help Section */}
                <Alert
                    message="Ví dụ chọn liên kết"
                    description={
                        <Space direction="vertical" size={4}>
                            <Text style={{ fontSize: '12px' }}>
                                Cho chọn liên kết 3 cấp (Tỉnh → Huyện → Xã):
                            </Text>
                            <Text code style={{ fontSize: '11px' }}>
                                1. Tỉnh: bảng=tbl_tinh, giá trị=ma_tinh, nhãn=ten_tinh
                            </Text>
                            <Text code style={{ fontSize: '11px' }}>
                                2. Huyện: bảng=tbl_huyen, giá trị=ma_huyen, nhãn=ten_huyen, where=ma_tinh=${'{tinh}'}
                            </Text>
                            <Text code style={{ fontSize: '11px' }}>
                                3. Xã: bảng=tbl_xa, giá trị=ma_xa, nhãn=ten_xa, where=ma_huyen=${'{huyen}'}
                            </Text>
                        </Space>
                    }
                    type="info"
                    showIcon
                />
            </Space>
        </Modal>
    );
};
